/**
 * Customer Identification and Bot Detection
 * Ensures real customer events are tracked properly and bots are filtered out
 */

(function() {
  'use strict';

  class CustomerIdentification {
    constructor() {
      this.isBot = this.detectBot();
      this.customerId = null;
      this.sessionId = this.generateSessionId();
      this.fingerprint = this.generateFingerprint();
      this.init();
    }

    init() {
      // Don't track if this is a bot
      if (this.isBot) {
        console.log('🤖 Bot detected, tracking disabled');
        return;
      }

      // Identify customer
      this.identifyCustomer();
      
      // Set up real user tracking
      this.setupRealUserTracking();
      
      // Monitor user interactions
      this.monitorUserActivity();
      
      console.log('👤 Real customer identified:', {
        customerId: this.customerId || 'Anonymous',
        sessionId: this.sessionId,
        fingerprint: this.fingerprint.substring(0, 8) + '...'
      });
    }

    detectBot() {
      const userAgent = navigator.userAgent.toLowerCase();
      
      // Common bot user agents
      const botPatterns = [
        'googlebot', 'bingbot', 'slurp', 'duckduckbot', 'baiduspider',
        'yandexbot', 'facebookexternalhit', 'twitterbot', 'linkedinbot',
        'whatsapp', 'telegram', 'skype', 'pinterest', 'reddit',
        'headlesschrome', 'phantomjs', 'slimerjs', 'selenium',
        'crawler', 'spider', 'bot', 'scraper'
      ];

      // Check user agent
      if (botPatterns.some(pattern => userAgent.includes(pattern))) {
        return true;
      }

      // Check for headless browser indicators
      if (window.navigator.webdriver) {
        return true;
      }

      // Check for missing typical browser features
      if (!window.chrome && !window.safari && !window.firefox && !window.opera) {
        // Might be a bot, but not conclusive
      }

      // Check for automation indicators
      if (window.phantom || window.callPhantom) {
        return true;
      }

      // Check for suspicious timing
      if (document.readyState === 'complete' && performance.now() < 100) {
        // Page loaded suspiciously fast
        return true;
      }

      return false;
    }

    generateSessionId() {
      return 'sess_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    }

    generateFingerprint() {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Browser fingerprint', 2, 2);
      
      return btoa(JSON.stringify({
        canvas: canvas.toDataURL(),
        screen: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack
      }));
    }

    identifyCustomer() {
      // Method 1: Shopify customer object
      if (window.customer && window.customer.id) {
        this.customerId = window.customer.id;
        return;
      }

      // Method 2: Shopify Analytics meta
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page) {
        this.customerId = window.ShopifyAnalytics.meta.page.customerId;
        return;
      }

      // Method 3: Meta tags
      const customerMeta = document.querySelector('meta[name="shopify-customer-id"]');
      if (customerMeta && customerMeta.content) {
        this.customerId = customerMeta.content;
        return;
      }

      // Method 4: Check for logged-in indicators
      const loginIndicators = [
        '.customer-logged-in',
        '[data-customer-id]',
        '.account-link[href*="account"]'
      ];

      for (const selector of loginIndicators) {
        const element = document.querySelector(selector);
        if (element) {
          this.customerId = element.dataset.customerId || 'logged_in_user';
          break;
        }
      }
    }

    setupRealUserTracking() {
      // Override analytics functions to include customer identification
      this.enhanceShopifyAnalytics();
      this.enhanceCustomerEvents();
      this.enhanceWebPixels();
    }

    enhanceShopifyAnalytics() {
      // ✅ PIXEL MANAGER COMPLIANCE: ShopifyAnalytics.lib override removed
      // Shopify Pixel Manager handles all native analytics automatically
      console.log('✅ Customer identification - using Pixel Manager for native events');
    }

    enhanceCustomerEvents() {
      // Enhance native Customer Events API
      if (window.Shopify && window.Shopify.customerEvents && window.Shopify.customerEvents.publish) {
        const originalPublish = window.Shopify.customerEvents.publish;
        
        window.Shopify.customerEvents.publish = (eventName, eventData) => {
          const enhancedData = {
            ...eventData,
            customerId: this.customerId,
            sessionId: this.sessionId,
            isRealCustomer: true,
            timestamp: new Date().toISOString()
          };

          console.log('👤 Enhanced Customer Event:', eventName, enhancedData);
          return originalPublish.call(this, eventName, enhancedData);
        };
      }

      // Enhance custom Customer Events
      if (window.shopifyCustomerEvents && window.shopifyCustomerEvents.publish) {
        const originalPublish = window.shopifyCustomerEvents.publish;
        
        window.shopifyCustomerEvents.publish = function(eventName, eventData) {
          const enhancedData = {
            ...eventData,
            customerId: this.customerId,
            sessionId: this.sessionId,
            isRealCustomer: true
          };

          return originalPublish.call(this, eventName, enhancedData);
        }.bind(this);
      }
    }

    enhanceWebPixels() {
      if (window.shopifyWebPixelTrack) {
        const originalTrack = window.shopifyWebPixelTrack;
        
        window.shopifyWebPixelTrack = (eventName, eventData) => {
          const enhancedData = {
            ...eventData,
            customerId: this.customerId,
            sessionId: this.sessionId,
            isRealCustomer: true,
            userFingerprint: this.fingerprint.substring(0, 16)
          };

          return originalTrack(eventName, enhancedData);
        };
      }
    }

    monitorUserActivity() {
      // Once we've seen 3 interactions we're done — remove all listeners immediately
      // so they never accumulate or fire network requests on every scroll/mousemove.
      let interactionCount = 0;

      const trackInteraction = () => {
        interactionCount++;
        if (interactionCount >= 3) {
          this.confirmRealUser();
          // Self-clean: remove every listener so no further calls occur
          document.removeEventListener('click', trackInteraction);
          document.removeEventListener('scroll', trackInteraction);
          document.removeEventListener('keydown', trackInteraction);
          document.removeEventListener('mousemove', trackInteraction);
          document.removeEventListener('touchstart', trackInteraction);
        }
      };

      // Use { passive: true } for scroll/mousemove to avoid blocking the main thread
      document.addEventListener('click', trackInteraction);
      document.addEventListener('scroll', trackInteraction, { passive: true });
      document.addEventListener('keydown', trackInteraction);
      document.addEventListener('mousemove', trackInteraction, { passive: true });
      document.addEventListener('touchstart', trackInteraction, { passive: true });
    }

    confirmRealUser() {
      // Mark this session as confirmed real user
      sessionStorage.setItem('confirmed_real_user', 'true');
      
      // ✅ PIXEL MANAGER COMPLIANCE: ShopifyAnalytics.lib.track removed
      // Shopify Pixel Manager handles user confirmation events natively
      if (window.ShopifyAnalytics) {
        console.log('✅ Real user confirmed - tracked via Pixel Manager');
      }

      console.log('✅ Real user confirmed through interactions');
    }

    // Public methods for external use
    isRealUser() {
      return !this.isBot && sessionStorage.getItem('confirmed_real_user') === 'true';
    }

    getCustomerId() {
      return this.customerId;
    }

    getSessionId() {
      return this.sessionId;
    }
  }

  // Initialize customer identification
  const customerIdentification = new CustomerIdentification();
  
  // Expose globally
  window.customerIdentification = customerIdentification;

  // Add helper method to check if tracking should proceed
  window.shouldTrackEvent = () => {
    return !customerIdentification.isBot;
  };

})();
